// src/components/products/ProductPricingForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchProduct,
  addPricingTier,
  updatePricingTier,
  clearProductError,
  clearProductSuccess,
} from '../../store/actions/productActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const ProductPricingForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, tierId } = useParams();
  const { selectedProduct: product, pricingTiers, loading, error, success } = useSelector(
    (state) => state.products
  );

  const [formData, setFormData] = useState({
    tierName: '',
    monthlyPremium: '',
    coverage: '',
    features: [],
    recommended: false,
    order: 0,
  });

  const [featureInput, setFeatureInput] = useState('');
  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!tierId;

  // Styles object
  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    productInfo: {
      marginBottom: '2rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    productName: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '2rem',
    },
    fieldGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '80px',
      resize: 'vertical',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    priceInput: {
      position: 'relative',
    },
    pricePrefix: {
      position: 'absolute',
      left: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    priceField: {
      paddingLeft: '4rem',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      marginRight: '0.5rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    featureInput: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '0.5rem',
    },
    featureTags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    featureTag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
    },
    removeFeature: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      fontSize: '0.875rem',
      padding: '0 0.25rem',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchProduct(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (isEditMode && pricingTiers) {
      const tier = pricingTiers.find(t => t.id === tierId);
      if (tier) {
        setFormData({
          tierName: tier.tierName || '',
          monthlyPremium: tier.monthlyPremium || '',
          coverage: tier.coverage || '',
          features: tier.features || [],
          recommended: tier.recommended || false,
          order: tier.order || 0,
        });
      }
    }
  }, [isEditMode, pricingTiers, tierId]);

  // Get next order number for new tiers
  useEffect(() => {
    if (!isEditMode && pricingTiers) {
      const maxOrder = pricingTiers.reduce((max, t) => Math.max(max, t.order || 0), 0);
      setFormData(prev => ({ ...prev, order: maxOrder + 1 }));
    }
  }, [isEditMode, pricingTiers]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearProductError());
      dispatch(clearProductSuccess());
    };
  }, [dispatch]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tierName.trim()) {
      newErrors.tierName = 'Le nom de la formule est requis';
    }

    if (!formData.monthlyPremium) {
      newErrors.monthlyPremium = 'La prime mensuelle est requise';
    } else if (parseFloat(formData.monthlyPremium) < 0) {
      newErrors.monthlyPremium = 'La prime ne peut pas être négative';
    }

    if (formData.order < 0) {
      newErrors.order = "L'ordre ne peut pas être négatif";
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : parseFloat(value),
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFeature();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const tierData = {
        ...formData,
        monthlyPremium: parseFloat(formData.monthlyPremium),
      };

      if (isEditMode) {
        await dispatch(updatePricingTier(id, tierId, tierData));
      } else {
        await dispatch(addPricingTier(id, tierData));
      }
      navigate(`/admin/products/${id}/pricing`);
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (formData.tierName || formData.monthlyPremium || formData.features.length > 0) {
      setShowCancelConfirm(true);
    } else {
      navigate(`/admin/products/${id}/pricing`);
    }
  };

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate(`/admin/products/${id}/pricing`)}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>
            {isEditMode ? 'Modifier le tarif' : 'Nouveau tarif'}
          </h1>
        </div>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {product && (
        <div style={styles.productInfo}>
          <div style={styles.productName}>
            Produit: {product.name}
          </div>
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearProductError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearProductSuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="tierName">
            Nom de la formule *
          </label>
          <input
            type="text"
            id="tierName"
            name="tierName"
            value={formData.tierName}
            onChange={handleChange}
            style={{
              ...styles.input,
              ...(errors.tierName ? styles.inputError : {}),
            }}
            placeholder="Ex: Formule Essentielle"
          />
          {errors.tierName && (
            <div style={styles.errorText}>{errors.tierName}</div>
          )}
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="monthlyPremium">
            Prime mensuelle (FCFA) *
          </label>
          <div style={styles.priceInput}>
            <input
              type="number"
              id="monthlyPremium"
              name="monthlyPremium"
              value={formData.monthlyPremium}
              onChange={handleNumberChange}
              min="0"
              step="1000"
              style={{
                ...styles.input,
                ...styles.priceField,
                ...(errors.monthlyPremium ? styles.inputError : {}),
              }}
              placeholder="5000"
            />
          </div>
          {errors.monthlyPremium && (
            <div style={styles.errorText}>{errors.monthlyPremium}</div>
          )}
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="coverage">
            Couverture
          </label>
          <textarea
            id="coverage"
            name="coverage"
            value={formData.coverage}
            onChange={handleChange}
            style={styles.textarea}
            placeholder="Description de la couverture offerte..."
            rows="3"
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Caractéristiques incluses</label>
          <div style={styles.featureInput}>
            <input
              type="text"
              value={featureInput}
              onChange={(e) => setFeatureInput(e.target.value)}
              onKeyPress={handleKeyPress}
              style={{ ...styles.input, flex: 1 }}
              placeholder="Ajouter une caractéristique"
            />
            <button
              type="button"
              onClick={handleAddFeature}
              style={styles.cancelButton}
            >
              Ajouter
            </button>
          </div>
          {formData.features.length > 0 && (
            <div style={styles.featureTags}>
              {formData.features.map((feature, index) => (
                <span key={index} style={styles.featureTag}>
                  {feature}
                  <button
                    type="button"
                    onClick={() => handleRemoveFeature(index)}
                    style={styles.removeFeature}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <div style={styles.helperText}>
            Ajoutez les caractéristiques incluses dans cette formule
          </div>
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="recommended"
              checked={formData.recommended}
              onChange={handleChange}
              style={styles.checkbox}
            />
            Formule recommandée
          </label>
          <div style={styles.helperText}>
            La formule recommandée sera mise en avant
          </div>
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="order">
            Ordre d'affichage
          </label>
          <input
            type="number"
            id="order"
            name="order"
            value={formData.order}
            onChange={handleNumberChange}
            min="0"
            style={{
              ...styles.input,
              ...(errors.order ? styles.inputError : {}),
            }}
          />
          {errors.order && (
            <div style={styles.errorText}>{errors.order}</div>
          )}
          <div style={styles.helperText}>
            Les formules sont triées par ordre croissant (les recommandées en premier)
          </div>
        </div>

        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={handleCancel}
            style={styles.cancelButton}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.submitButton,
              ...(isSubmitting ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {isSubmitting 
              ? (isEditMode ? 'Modification...' : 'Ajout...') 
              : (isEditMode ? 'Mettre à jour' : 'Ajouter')}
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate(`/admin/products/${id}/pricing`)}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer"
        type="warning"
      />
    </div>
  );
};

export default ProductPricingForm;